import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";

/*
 * Subtle-but-flashy idle: slow heartbeat scale (1.0 → 1.05) + two staggered
 * sonar halos pinging out every 2.4s + arrow nudge. On click: snap-shrink-
 * then-pop. When the last section is in view (atEnd=true), the arrow
 * rotates 180° and the click handler scrolls back to top instead of forward.
 * Pure CSS keyframes — no JS animation loops.
 */

const idlePulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.05); }
`;

const arrowNudge = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(2px); }
`;

const sonar = keyframes`
  0%   { transform: scale(0.9); opacity: 0.55; }
  80%  { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(2.2); opacity: 0; }
`;

const clickPop = keyframes`
  0%   { transform: scale(1); }
  35%  { transform: scale(0.85); }
  70%  { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

const Wrap = styled.div`
  position: fixed;
  right: 18px;
  bottom: 18px;
  width: 48px;
  height: 48px;
  z-index: 999;
  display: none;
  @media (max-width: 768px) { display: block; }
`;

const Halo = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--nd-text-display);
  opacity: 0;
  pointer-events: none;
  animation: ${sonar} 2.4s cubic-bezier(0.16, 1, 0.3, 1) infinite;
  animation-delay: ${({ $delay }) => $delay || "0s"};
`;

const Btn = styled.button`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: var(--nd-text-display);
  color: var(--nd-black);
  border: 0;
  font-family: 'Space Mono', monospace;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.5);
  transform-origin: center;
  animation: ${idlePulse} 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  ${({ $popping }) =>
    $popping &&
    css`
      animation: ${clickPop} 360ms cubic-bezier(0.25, 0.1, 0.25, 1);
    `}

  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid var(--nd-accent); outline-offset: 3px; }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Two-layer transform: outer rotates on atEnd, inner runs the nudge
// animation (which mutates `transform` and would otherwise clobber the
// rotate). Splitting them keeps both transforms compositing cleanly.
const ArrowRotator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: ${({ $flip }) => ($flip ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 400ms cubic-bezier(0.25, 0.1, 0.25, 1);
`;

const Arrow = styled.span`
  display: inline-block;
  font-weight: 700;
  line-height: 1;
  animation: ${arrowNudge} 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Universal end-of-page detector — works on pages with or without
// <section> / [data-section] landmarks. The viewport bottom is within
// BOTTOM_SLOP of the document bottom counts as "at end".
const BOTTOM_SLOP = 120;

const isAtBottom = () => {
  if (typeof window === "undefined") return false;
  const docHeight = Math.max(
    document.documentElement.scrollHeight || 0,
    document.body ? document.body.scrollHeight : 0,
  );
  return window.scrollY + window.innerHeight >= docHeight - BOTTOM_SLOP;
};

const NextSectionButton = () => {
  const [atEnd, setAtEnd] = useState(false);
  const [popping, setPopping] = useState(false);
  const popTimer = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onScroll = () => setAtEnd(isAtBottom());
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(
    () => () => {
      if (popTimer.current) clearTimeout(popTimer.current);
    },
    [],
  );

  const onClick = () => {
    setPopping(true);
    if (popTimer.current) clearTimeout(popTimer.current);
    popTimer.current = setTimeout(() => setPopping(false), 380);

    if (atEnd) {
      // Reached document bottom → back to top.
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Prefer the next <section> / [data-section] boundary so multi-section
    // marketing pages snap cleanly. Fall back to a one-viewport scroll on
    // pages without those landmarks (admin dashboards, sales leads, etc).
    const sections = Array.from(document.querySelectorAll("section, [data-section]"));
    const y = window.scrollY;
    const next = sections.find((s) => {
      const top = s.getBoundingClientRect().top + y;
      return top > y + 80;
    });
    if (next) {
      next.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const step = Math.max(200, window.innerHeight - 80);
    window.scrollTo({ top: y + step, behavior: "smooth" });
  };

  return (
    <Wrap>
      <Halo aria-hidden="true" />
      <Halo aria-hidden="true" $delay="1.2s" />
      <Btn
        type="button"
        onClick={onClick}
        aria-label={atEnd ? "Back to top" : "Next section"}
        $popping={popping}
      >
        <ArrowRotator $flip={atEnd}>
          <Arrow>↓</Arrow>
        </ArrowRotator>
      </Btn>
    </Wrap>
  );
};

export default NextSectionButton;
